import { StatusGanttVariantType } from "@components/index";
import { StatusFilterType } from "@pages/Dashboard/contexts/Filter.context";
import { getEventStatusVariant } from "./getEventStatusForFilter";

/**
 * Verifica se o evento passa pelo filtro de status
 * Filtra baseado no variant do status (não no valor do status)
 */
function matchesStatusFilter(
  event: Sentinel.Timeline.Event,
  statusVariantFilters?: StatusFilterType[]
): boolean {
  // Se não há filtros, mostra todos os eventos
  if (!statusVariantFilters?.length) return true;

  const hasLateFilter = statusVariantFilters.includes("late");
  const variantFilters = statusVariantFilters.filter((f): f is StatusGanttVariantType => f !== "late");

  if (hasLateFilter) {
    const isLate = event.displayProps?.showLateIcon ?? false;
    if (isLate) return true;
  }

  if (variantFilters.length > 0) {
    const eventStatusVariant = getEventStatusVariant(event);
    if (eventStatusVariant && variantFilters.includes(eventStatusVariant)) {
      return true;
    }
  }

  if (hasLateFilter && variantFilters.length === 0) {
    return false;
  }

  return false;
}

/**
 * Verifica se o evento passa pelo filtro de canais
 */
function matchesChannelFilter(
  event: Sentinel.Timeline.Event,
  channelFilters?: string[]
): boolean {
  if (!channelFilters?.length) return true;

  const eventChannels = event.channels?.map((c) => c.name.toUpperCase()) || [];
  const filterChannels = channelFilters.map((c) => c.toUpperCase());

  return filterChannels.some((filterChannel) =>
    eventChannels.includes(filterChannel)
  );
}

/**
 * Verifica se um evento passa por todos os filtros aplicados
 */
function shouldIncludeEvent(
  event: Sentinel.Timeline.Event,
  statusVariantFilters?: StatusFilterType[],
  channelFilters?: string[]
): boolean {
  const passesStatus = matchesStatusFilter(event, statusVariantFilters);
  const passesChannel = matchesChannelFilter(event, channelFilters);
  return passesStatus && passesChannel;
}

/**
 * Aplica filtros de status (variants) e canais aos eventos
 */
export function applyFiltersToEvents(
  events: Sentinel.Timeline.Event[],
  statusVariantFilters?: StatusFilterType[],
  channelFilters?: string[]
): Sentinel.Timeline.Event[] {
  if (!events?.length) return events || [];

  const filtered: Sentinel.Timeline.Event[] = [];
  for (const event of events) {
    if (shouldIncludeEvent(event, statusVariantFilters, channelFilters)) {
      filtered.push(event);
    }
  }
  return filtered;
}

/**
 * Filtra grupos de eventos, removendo grupos vazios
 */
function filterGroupedEvents(
  groupedEvents: Sentinel.Timeline.GroupedEvent[],
  statusVariantFilters?: StatusFilterType[],
  channelFilters?: string[]
): Sentinel.Timeline.GroupedEvent[] {
  const filtered: Sentinel.Timeline.GroupedEvent[] = [];

  for (const group of groupedEvents) {
    const filteredEvents = applyFiltersToEvents(
      group.events,
      statusVariantFilters,
      channelFilters
    );

    if (filteredEvents.length > 0) {
      filtered.push({
        ...group,
        events: filteredEvents,
      });
    }
  }

  return filtered;
}

/**
 * Filtra lanes de eventos, removendo lanes vazias
 */
function filterLanedEvents(
  lanedEvents: Sentinel.Timeline.LanedEvent[],
  statusVariantFilters?: StatusFilterType[],
  channelFilters?: string[]
): Sentinel.Timeline.LanedEvent[] {
  const filtered: Sentinel.Timeline.LanedEvent[] = [];

  for (const lane of lanedEvents) {
    const filteredGroups = filterGroupedEvents(
      lane.groupedEvents,
      statusVariantFilters,
      channelFilters
    );

    if (filteredGroups.length > 0) {
      filtered.push({
        ...lane,
        groupedEvents: filteredGroups,
      });
    }
  }

  return filtered;
}

/**
 * Aplica filtros aos dados do timeline (lanedEvents) na origem
 * Filtra antes de calcular intervals e posições
 * Esta função deve ser chamada antes de passar os dados para useMapDataTimeline
 */
export function applyFiltersToTimelineData(
  data: Sentinel.Timeline.New,
  statusVariantFilters?: StatusFilterType[],
  channelFilters?: string[]
): Sentinel.Timeline.New {
  if (!data?.lanedEvents) return data;

  const filteredLanes = filterLanedEvents(
    data.lanedEvents,
    statusVariantFilters,
    channelFilters
  );

  return {
    ...data,
    lanedEvents: filteredLanes,
  };
}
